/**
 * useWorkOrderDeleteFlow — the shared state machine behind BOTH work-order
 * delete surfaces (WO header "Delete work order" and the /workorders row
 * Delete). One place owns: open/close, the delete-preview fetch that feeds
 * the confirm dialog, the DELETE call, and the P2-8/P2-9 409 ceremony
 * (SCENARIO_FINALIZED / SOURCE_RELEASES_COMMITTED → flip the dialog into
 * "Release schedule & delete" for ONE explicit re-confirm).
 */
import { useCallback, useState } from 'react';
import { deleteWorkOrder, getWorkOrderDeletePreview } from '../services/workOrderService';
import type { WorkOrderDeletePreview, WorkOrderDeleteResult } from '../types/workOrder';

export interface WorkOrderDeleteTarget {
  workOrderKey: string;
  workOrderId: string;
  workOrderName: string | null;
  projectId: string | null;
}

interface UseWorkOrderDeleteFlowArgs {
  /** Called after a successful delete with the cascade counts. */
  onDeleted: (result: WorkOrderDeleteResult, target: WorkOrderDeleteTarget) => void;
}

export interface WorkOrderDeleteFlow {
  target: WorkOrderDeleteTarget | null;
  /** Live inventory from the server; null until the probe resolves (dialog shows the caller's counts meanwhile). */
  preview: WorkOrderDeletePreview | null;
  busy: boolean;
  error: string | null;
  /** Set after a schedule-guard 409 — the next confirm sends releaseSchedule=true. */
  forceRelease: boolean;
  openFor: (target: WorkOrderDeleteTarget) => void;
  close: () => void;
  confirm: (releaseSchedule: boolean) => Promise<void>;
}

export function useWorkOrderDeleteFlow({ onDeleted }: UseWorkOrderDeleteFlowArgs): WorkOrderDeleteFlow {
  const [target, setTarget] = useState<WorkOrderDeleteTarget | null>(null);
  const [preview, setPreview] = useState<WorkOrderDeletePreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [forceRelease, setForceRelease] = useState(false);

  const openFor = useCallback((next: WorkOrderDeleteTarget) => {
    setTarget(next);
    setPreview(null);
    setError(null);
    setForceRelease(false);
    getWorkOrderDeletePreview(next.workOrderKey)
      .then((p) => setPreview((cur) => (cur === null ? p : cur)))
      .catch(() => { /* the dialog still works from the caller's counts */ });
  }, []);

  const close = useCallback(() => setTarget(null), []);

  const confirm = useCallback(async (releaseSchedule: boolean) => {
    if (!target) return;
    setBusy(true);
    setError(null);
    try {
      const result = await deleteWorkOrder(
        target.workOrderKey,
        releaseSchedule ? { releaseSchedule: true } : undefined,
      );
      setTarget(null);
      onDeleted(result, target);
    } catch (err) {
      // Surface the server's human message; schedule-guard 409s flip the
      // dialog into "Release schedule & delete" for one explicit re-confirm.
      const resp = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { error?: string; code?: string } } }).response
        : undefined;
      const code = resp?.data?.code;
      setError(resp?.data?.error ?? (err instanceof Error ? err.message : 'Failed to delete work order'));
      if (code === 'SCENARIO_FINALIZED' || code === 'SOURCE_RELEASES_COMMITTED') setForceRelease(true);
    } finally {
      setBusy(false);
    }
  }, [target, onDeleted]);

  return { target, preview, busy, error, forceRelease, openFor, close, confirm };
}

/** One-line success toast for a completed delete: what was removed, released and unlinked. */
export function describeWorkOrderDeleteResult(workOrderId: string, r: WorkOrderDeleteResult): string {
  const parts: string[] = [`${r.detailsDeleted} test row${r.detailsDeleted === 1 ? '' : 's'} removed`];
  if (r.bookingsReleased > 0 || r.tasksReleased > 0) {
    parts.push(`${r.bookingsReleased} booking${r.bookingsReleased === 1 ? '' : 's'} released`);
  }
  if (r.scenariosDeleted > 0) parts.push(`${r.scenariosDeleted} plan${r.scenariosDeleted === 1 ? '' : 's'} removed`);
  if (r.quotesUnlinked.length > 0) {
    parts.push(`quote ${r.quotesUnlinked.map((q) => q.quoteNumber).join(', ')} unlinked`);
  }
  return `Work order ${workOrderId} deleted — ${parts.join(', ')}.`;
}
